import { useCallback, useEffect, useMemo, useState } from "react";
import { can } from "../../../shared/auth/access";
import type { SessionUser } from "../../../shared/auth/access";
import type { ProductionRun, TerminalAdministration } from "../models";
import { productionGateway } from "../production-gateway";
import { useProductionData } from "../useProductionData";
import { useActiveShift } from "../useActiveShift";
import { today } from "../production-time";
import { runsForShift } from "../plan-flow-reporting";
import { operatorLineSummaries } from "../operator-report-summary";
import { decideAndRefresh } from "../supervisor-decision-request";
import { supervisorBoardModel } from "./supervisor-board-model";
import type { ManagerReportView, SupervisorLineFilter } from "./supervisor-board-model";
import type { SupervisorDecisionAction } from "./supervisor-decision-model";

export type SupervisorBoardOptions = {
  user: SessionUser;
  notify: (message: string, tone?: "success" | "error") => void;
  dataRefreshVersion: number;
};

export function useSupervisorBoardWorkspace({ user, notify, dataRefreshVersion }: SupervisorBoardOptions) {
  const { reference, runs, operatorReport, notifications, loading, updatedAt, loadError, load } = useProductionData(
    user,
    notify,
    dataRefreshVersion,
  );
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [decisionTarget, setDecisionTarget] = useState<ProductionRun | null>(null);
  const [directDecision, setDirectDecision] = useState<SupervisorDecisionAction | undefined>();
  const [historicalCorrectionOpen, setHistoricalCorrectionOpen] = useState(false);
  const [managerReportView, setManagerReportView] = useState<ManagerReportView>("overview");
  const [terminalAdmin, setTerminalAdmin] = useState<TerminalAdministration | null>(null);
  const [assignmentOpen, setAssignmentOpen] = useState(false);
  const [terminalRepairLineId, setTerminalRepairLineId] = useState<string | null>(null);
  const closeTerminalRepair = useCallback(() => setTerminalRepairLineId(null), []);
  const refreshTerminalAdmin = useCallback(async () => {
    if (!can(user, "production.assignment.manage") && !can(user, "production.terminal.manage") && !can(user, "production.run.manage")) return;
    try { setTerminalAdmin(await (can(user, "production.assignment.manage") ? productionGateway.terminalAdministration() : productionGateway.terminalShiftOverview())); }
    catch (error) { notify(error instanceof Error ? error.message : "Makine atamaları yüklenemedi."); }
  }, [notify, user]);
  // Production runs refresh every 15 seconds and from the header action. Refresh
  // shift assignments from the same data boundary so a newly assigned operator
  // cannot remain "unassigned" in the supervisor view until the page remounts.
  useEffect(() => {
    if (updatedAt !== null) void refreshTerminalAdmin();
  }, [dataRefreshVersion, refreshTerminalAdmin, updatedAt]);
  const [lineFilter, setLineFilter] = useState<SupervisorLineFilter>("all");
  useEffect(() => {
    if (!summaryOpen) return;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSummaryOpen(false);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [summaryOpen]);
  const activeShiftCode = useActiveShift();
  const shiftRuns = useMemo(
    () => runsForShift(runs, today(), activeShiftCode, reference),
    [activeShiftCode, runs, reference],
  );
  const shiftReport = useMemo(() => operatorLineSummaries(shiftRuns), [shiftRuns]);
  const closeDecision = () => { setDecisionTarget(null); setDirectDecision(undefined); };
  const openDecision = (run: ProductionRun, action?: SupervisorDecisionAction) => {
    setDirectDecision(action); setDecisionTarget(run);
  };
  const reviewRun = async (run: ProductionRun, note: string) => {
    await productionGateway.decideRun(run.id, { action: "COMPLAINT_REVIEWED", note, expectedVersion: run.version });
    await load(); notify("İnceleme notu kaydedildi.");
  };
  const submitDecision = async (form: Record<string, unknown>, signal: AbortSignal) => {
    if (!decisionTarget) return;
    await decideAndRefresh(() => productionGateway.decideRun(decisionTarget.id, form, signal).then(() => undefined), load);
    closeDecision();
    const action = String(form.action);
    notify(action === "SHIFT_TRANSFERRED" ? `İş ${String(form.shiftCode)} planına devredildi. Operatör kuyruğu ve vardiya devir özeti güncellendi. Karar iş geçmişine kaydedildi.` : action === "FORCED_COMPLETION" ? "İş zorunlu olarak tamamlandı. Hareket ve karar geçmişi güncellendi." : "Karar kaydedildi. Operatör kuyruğu, vardiya devri ve iş geçmişi güncellendi.");
  };
  const dataIsFresh = () => !loadError && updatedAt !== null && Date.now() - updatedAt <= 60_000;
  const toggleLineFilter = (filter: Exclude<SupervisorLineFilter, "all">) =>
    setLineFilter((current) => (current === filter ? "all" : filter));
  const model = supervisorBoardModel(shiftRuns, shiftReport, reference, today(), activeShiftCode, lineFilter, Date.now());
  return {
    reference, runs, operatorReport, notifications, loading, updatedAt, loadError, load,
    summaryOpen, setSummaryOpen, decisionTarget, directDecision, historicalCorrectionOpen, setHistoricalCorrectionOpen,
    managerReportView, setManagerReportView, terminalAdmin, assignmentOpen, setAssignmentOpen,
    terminalRepairLineId, setTerminalRepairLineId, closeTerminalRepair, refreshTerminalAdmin,
    lineFilter, setLineFilter, toggleLineFilter, activeShiftCode, shiftRuns, shiftReport, model,
    openDecision, closeDecision, reviewRun, submitDecision, dataIsFresh,
  };
}

export type SupervisorBoardWorkspace = ReturnType<typeof useSupervisorBoardWorkspace>;
